import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import ResourceTabCard, { ResourceRow } from './ResourceTabCard';
import ResourceHeaderCard from './ResourceHeaderCard';
import AddResourceCard from './AddResourceCard';
import { ASSET_STATUS_FILTER_OPTIONS } from './resourceFilterOptions';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import {
  GroupRosterItem,
  useGetRostersForGroup,
} from 'hooks/incident/useRoster';
import { useAors } from 'hooks/useAors';
import ReactPaginateImport from 'react-paginate';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { USCGResourceKindOptions } from 'components/IncidentConfig/ResourceTypes';
import {
  ResourceSet,
  type ResourceSetFilterSelection,
  usePaginatedResourceSets,
} from '../../../IAP/hooks/resourceSetHooks';
import CheckInDrawer from 'components/IAP/Resources/Drawers/CheckInDrawer';
import CheckOutDrawer from 'components/IAP/Resources/Drawers/CheckOutDrawer';
import { RESOURCE_STATUSES } from 'components/IAP/Resources/constants/resourceConstants';
import {
  useResourcesInIncident,
  ResourceInIncident,
} from 'components/IAP/hooks/resourceInIncidentHooks';

const ReactPaginate =
  (ReactPaginateImport as any).default ?? ReactPaginateImport;

const RESOURCES_PER_PAGE = 10;
const COMPACT_GRID = 'compact-resources-card-header';
const WORK_AVAILABILITY_FILTER_OPTIONS = [
  'In Organization',
  'Not Arrived',
  'In Transit',
  'On Site',
  'Checked In',
  'Demobilized',
];
const REQUEST_STATUS_FILTER_OPTIONS = ['Requested', 'Not Requested'];

export const mapResourceSetToResourceRow = (
  resourceSet: ResourceSet,
  groupMembers?: GroupRosterItem[]
): ResourceRow => {
  const pocMember =
    resourceSet.point_of_contact && groupMembers
      ? groupMembers.find((m) => m.user_guid === resourceSet.point_of_contact)
      : undefined;

  return {
    id: resourceSet.id,
    resource_set_id: resourceSet.resource_set_id,
    name: resourceSet.name,
    kind: resourceSet.kind,
    type: resourceSet.type || '',
    assetStatus: resourceSet.asset_status || '',
    assetStatusTimestamp: resourceSet.asset_status_timestamp || '',
    incident: resourceSet.incident_details?.[0]
      ? {
          id: resourceSet.incident_details[0].id,
          name: resourceSet.incident_details[0].name,
        }
      : null,
    incident_details: resourceSet.incident_details,
    owning_organization: resourceSet.owning_organization || '',
    description: resourceSet.description,
    datetimeOrdered: resourceSet.date_time_ordered,
    datetimeOrderedOffset: resourceSet.datetime_ordered_offset,
    quantity: resourceSet.quantity,
    status: resourceSet.current_status || resourceSet.status,
    inRequest: resourceSet.in_request,
    aor: resourceSet.aor,
    opcon: resourceSet.opcon,
    tacon: resourceSet.tacon,
    persons_on_board: resourceSet.persons_on_board,
    hull_tail_number: resourceSet.hull_tail_number,
    unit: resourceSet.unit,
    address: resourceSet.address,
    symbology: resourceSet.symbology,
    latitude: resourceSet.geometry?.coordinates[1],
    longitude: resourceSet.geometry?.coordinates[0],
    assignees: resourceSet.resource_in_incident_details
      ? resourceSet.resource_in_incident_details.flatMap((detail) =>
          (detail.assignees || []).map((assignee) => ({
            userGuid: assignee.id,
            userName: assignee.name,
            emailAddress: assignee.email,
          }))
        )
      : [],
    eta: resourceSet.eta,
    etaTimezoneOffset: resourceSet.eta_timezone_offset,
    notes: resourceSet.notes,
    pointOfContact: pocMember
      ? {
          userGuid: pocMember.user_guid,
          emailAddress: pocMember.email_address,
          userName: pocMember.user_name,
        }
      : undefined,
  };
};

const ResourceView: React.FC<{
  showAddResourceCard: boolean;
  setShowAddResourceCard: (show: boolean) => void;
  handleResourceUpdate: (updatedResource: Partial<ResourceInIncident>) => void;
  searchTerm: string;
  compact?: boolean;
}> = ({
  showAddResourceCard,
  setShowAddResourceCard,
  handleResourceUpdate,
  searchTerm,
  compact,
}) => {
  const [selectedResourceInIncident, setSelectedResourceInIncident] =
    useState<ResourceInIncident | null>(null);
  const [showCheckInDrawer, setShowCheckInDrawer] = useState(false);
  const [showCheckOutDrawer, setShowCheckOutDrawer] = useState(false);
  const { data: resourcesInIncident } = useResourcesInIncident();
  const { data: incidents = [] } = useAllIncidents();
  const { data: aors = [] } = useAors();

  const groupId = useGroupGuid();
  // Fetch group members
  const { data: groupMembers = [] } = useGetRostersForGroup(groupId);

  const kindFilterOptions = useMemo(
    () => USCGResourceKindOptions.map((option) => option.value),
    []
  );
  const aorFilterOptions = useMemo(
    () => Array.from(new Set(aors.map((aor) => aor.name).filter(Boolean))),
    [aors]
  );
  const incidentFilterOptions = useMemo(
    () =>
      incidents
        .filter((incident) => !!incident.id && !!incident.name)
        .map((incident) => ({ value: incident.id, label: incident.name })),
    [incidents]
  );

  const columns = useMemo(() => {
    if (compact) {
      return [
        {
          key: 'id',
          label: 'ID',
        },
        {
          key: 'name',
          label: 'Resource Name',
          filterOptions: [],
        },
        {
          key: 'assetStatus',
          label: 'Asset Status',
          filterOptions: ASSET_STATUS_FILTER_OPTIONS,
        },
      ];
    }
    const generatedColumns = [
      {
        key: 'id',
        label: 'ID',
      },
      {
        key: 'name',
        label: 'Resource Name',
        filterOptions: [],
      },
      {
        key: 'kind',
        label: 'Kind',
        filterOptions: kindFilterOptions,
      },
      {
        key: 'aor',
        label: 'AOR',
        filterOptions: aorFilterOptions,
      },
      {
        key: 'incident',
        label: 'Incident',
        filterOptions: incidentFilterOptions,
      },
      {
        key: 'workAvailability',
        label: 'Current Operational Period Work Availability',
        filterOptions: WORK_AVAILABILITY_FILTER_OPTIONS,
      },
      {
        key: 'requestStatus',
        label: 'Request Status',
        filterOptions: REQUEST_STATUS_FILTER_OPTIONS,
      },
      {
        key: 'assetStatus',
        label: 'Asset Status',
        filterOptions: ASSET_STATUS_FILTER_OPTIONS,
      },
    ];
    return generatedColumns;
  }, [compact, aorFilterOptions, incidentFilterOptions, kindFilterOptions]);

  // In the incident workspace (/iap/:incidentId) default the Incident filter to the current incident so the table
  // opens scoped to it. The raw route param (not the Redux fallback) keeps the corporate/map view unfiltered.
  const { incidentId: routeIncidentId } = useParams<{ incidentId: string }>();
  const [filterSelection, setFilterSelection] =
    useState<ResourceSetFilterSelection>({
      name: [],
      kind: [],
      aor: [],
      incident: routeIncidentId ? [routeIncidentId] : [],
      workAvailability: [],
      requestStatus: [],
      assetStatus: [],
      resultsPerPage: RESOURCES_PER_PAGE,
      pageNumber: 1,
    });

  const { data: resourceSetsPage } = usePaginatedResourceSets(filterSelection);

  const memoizedResources = useMemo<ResourceRow[]>(() => {
    return (resourceSetsPage?.rows ?? [])
      .filter((resourceSet) =>
        resourceSet.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .map((resourceSet) =>
        mapResourceSetToResourceRow(resourceSet, groupMembers)
      );
  }, [resourceSetsPage, searchTerm, groupMembers]);

  const handleFilterChange = (filterType: string, values: string[]) => {
    if (!(filterType in filterSelection)) {
      return;
    }
    setFilterSelection((prev) => ({
      ...prev,
      [filterType]: values,
      pageNumber: 1,
    }));
  };

  const updateCurrentPage = (pageNumber: number) => {
    setFilterSelection((prev) => ({
      ...prev,
      pageNumber,
      resultsPerPage: RESOURCES_PER_PAGE,
    }));
  };

  return (
    <>
      <div>
        <ResourceHeaderCard
          filters={(({ resultsPerPage: _rpp, pageNumber: _pn, ...rest }) =>
            rest)(filterSelection)}
          onFilterChange={handleFilterChange}
          columns={columns}
          grid={compact ? COMPACT_GRID : undefined}
        />
        {showAddResourceCard && (
          <AddResourceCard
            setShowAddResourceCard={setShowAddResourceCard}
            groupMembers={groupMembers}
            compact={compact}
          />
        )}
        {memoizedResources?.length === 0 && !showAddResourceCard && (
          <div className="text-muted py-4 text-center">No results</div>
        )}
        {memoizedResources?.map((row) => (
          <ResourceTabCard
            key={row.id}
            row={row}
            groupMembers={groupMembers}
            compact={compact}
            setShowCheckInDrawer={setShowCheckInDrawer}
            setShowCheckOutDrawer={setShowCheckOutDrawer}
            setSelectedResource={(set) => {
              const selectedResourceInIncident = resourcesInIncident?.find(
                (r) => r.resource_set_id === set
              );
              if (selectedResourceInIncident) {
                setSelectedResourceInIncident(selectedResourceInIncident);
              }
            }}
          />
        ))}
        {!!resourceSetsPage?.totalPages && resourceSetsPage.totalPages > 1 && (
          <div className="ms-auto">
            <ReactPaginate
              previousLabel={
                <SharedIcon
                  iconName="chevron_left"
                  size="md"
                  classes="d-flex"
                />
              }
              nextLabel={
                <SharedIcon
                  iconName="chevron_right"
                  size="md"
                  classes="d-flex"
                />
              }
              breakLabel={'...'}
              pageCount={resourceSetsPage ? resourceSetsPage.totalPages : 1}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={(sel: any) => {
                updateCurrentPage(sel.selected + 1);
              }}
              forcePage={(resourceSetsPage?.pageNumber || 1) - 1}
              containerClassName={'pagination mb-0 '}
              pageClassName={'page-item'}
              pageLinkClassName={'page-link'}
              previousClassName={'page-item'}
              previousLinkClassName={'page-link'}
              nextClassName={'page-item'}
              nextLinkClassName={'page-link'}
              breakClassName={'page-item'}
              breakLinkClassName={'page-link'}
              activeClassName={'active'}
            />
          </div>
        )}
      </div>
      {showCheckInDrawer && selectedResourceInIncident && (
        <CheckInDrawer
          resource={selectedResourceInIncident}
          onClose={() => {
            setShowCheckInDrawer(false);
          }}
          onSave={(data) => {
            handleResourceUpdate({
              ...selectedResourceInIncident,
              ...data,
              current_status: RESOURCE_STATUSES.AVAILABLE,
              status_since: new Date().toISOString(),
            });
            setShowCheckInDrawer(false);
          }}
        />
      )}
      {showCheckOutDrawer && selectedResourceInIncident && (
        <CheckOutDrawer
          resource={selectedResourceInIncident}
          onClose={() => {
            setShowCheckOutDrawer(false);
          }}
          onSave={(data: any) => {
            handleResourceUpdate({
              ...selectedResourceInIncident,
              ...data,
              current_status: RESOURCE_STATUSES.CHECKED_OUT,
            });
            setShowCheckOutDrawer(false);
          }}
        />
      )}
    </>
  );
};

export default ResourceView;
